import type { MaybeRef } from "vue"
import type { ServerResponse, ServerResponseV1 } from "@/http"
import type { ICommentResponse, ICommentResponseV1, Media } from "@/models"
import { useInfiniteQuery } from "@tanstack/vue-query"
import { computed, toValue } from "vue"
import { axios, v1 } from "@/axios"
import { Comment, MediaType } from "@/models"
import { ContentType } from "@/types"

export interface IQueryParams {
  contentId?: number
  contentType?: number
  perPage?: number
  page?: number
}

export async function listComments(eventVaultId?: number, { page, perPage, contentId, contentType }: IQueryParams = {}) {
  if (!eventVaultId) {
    throw new Error(
      "Missing `eventVaultId` when attempting to list comments. Please ensure `eventVaultId` is provided.",
    )
  }

  if (!contentId) {
    throw new Error("Missing `contentId` when attempting to list comments. Please ensure `contentId` is provided.")
  }

  if (!contentType && contentType !== 0) {
    throw new Error("Missing `contentType` when attempting to list comments. Please ensure `contentType` is provided.")
  }

  const query = new URLSearchParams()

  query.append("contentId", contentId.toString())
  query.append("contentType", contentType.toString())
  if (page)
    query.append("page", page.toString())
  if (perPage)
    query.append("perPage", perPage.toString())

  const endpoint = `api/${eventVaultId}/comments?${query.toString()}`

  const response = await axios.get<ServerResponse<ICommentResponse[]>>(endpoint)

  return {
    comments: response.data.Data?.map(comment => Comment.fromResponse(comment)) ?? [],
    links: response.data.Links,
  }
}

export async function listCommentsV1(eventVaultId: number, { contentId, contentType, page, perPage }: IQueryParams = {}) {
  if (!contentId) {
    throw new Error("Missing `contentId` when attempting to list comments. Please ensure `contentId` is provided.")
  }

  if (!contentType && contentType !== 0) {
    throw new Error("Missing `contentType` when attempting to list comments. Please ensure `contentType` is provided.")
  }

  const query = new URLSearchParams()

  query.append("contentId", contentId.toString())
  query.append("contentType", contentType.toString())
  if (page)
    query.append("page", page.toString())
  if (perPage)
    query.append("perPage", perPage.toString())

  const response = await v1.get<ServerResponseV1<ICommentResponseV1[]>>(`/${eventVaultId}/comments`, {
    params: query,
  })

  return {
    comments: response.data.data?.map(comment => Comment.fromResponseV1(comment)) ?? [],
    links: response.data.links,
    meta: response.data.meta,
  }
}

export function useListCommentsV1(
  eventVaultId?: MaybeRef<number | undefined>,
  media?: MaybeRef<Media | undefined>,
  isEnabled?: MaybeRef<boolean>,
  options: { perPage?: number } = {},
) {
  const { perPage = 5 } = options

  const query = useInfiniteQuery({
    queryKey: ["listComments", eventVaultId, media],
    queryFn: async ({ pageParam }) => {
      const vaultId = toValue(eventVaultId)
      const mediaValue = toValue(media)

      if (!vaultId || !mediaValue?.id) {
        throw new Error("Missing required parameters: eventVaultId and media.id")
      }

      return await listCommentsV1(vaultId, {
        page: pageParam ?? 1,
        perPage,
        contentId: mediaValue.id,
        contentType: mediaValue.type === MediaType.Vid ? ContentType.Video : ContentType.Photo,
      })
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.meta?.hasNext) {
        return lastPage.meta?.currentPage !== undefined ? lastPage.meta.currentPage + 1 : undefined
      }
      return undefined
    },
    initialPageParam: 1,
    enabled: computed(() => !!toValue(eventVaultId) && !!toValue(media)?.id && !!toValue(isEnabled)),
  })

  const commentItems = computed<Comment[]>(() => query.data.value?.pages.flatMap(p => p.comments) ?? [])

  return {
    ...query,
    commentItems,
  }
}
